import { FCX } from "@datassential/platform-ui-lib";
import {
    HomeTableRawNormDataItem,
    HomeTableRawNormRequestPayload,
    useHomeTableRawNormData
} from "@api/useHomeTableRawNormData";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { HomeShow } from "@core/constants";
import {
    TableViewRawNormTableConfigProps,
    useTableViewRawNormTableConfig
} from "@pages/Home/components/TableView/TableViewRawNormTableConfig";
import { TableColumnsContext } from "@components/Table/TableColumns";
import WindowScrollerTable from "@components/Table/WindowScrollerTable";
import { useFilteredKeywordsData } from "@api/useFilteredKeywordsData";
import { useHomeContext, useHomeSearchSettingsContext } from "@pages/Home/HomeContext";
import { useFilterSort } from "@hooks/useFilterSort";
import { Sort } from "@models";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { HOME_TABLE_METRICS_POLARITY_ORDER } from "@models/Metric";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import { useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";
import { useSelectFilters } from "@vms/SelectFilters/SelectFiltersContext";
import HomeHeader from "@pages/Home/components/HomeHeader/HomeHeader";
import SelectShow from "@pages/Home/components/HomeHeader/SelectShow";
import HomeSubHeader from "@pages/Home/components/HomeHeader/HomeSubHeader";
import SelectAmong from "@vms/SelectAmong";
import SelectVersus from "@vms/SelectVersus";
import HomeActionsBar from "@pages/Home/components/HomeActionsBar/HomeActionsBar";
import { ProfileExportPptRequest } from "@pages/Profile/components/Header/Header";

const TableViewRawNorm: FCX<{
    show: HomeShow;
    setShow: Dispatch<SetStateAction<HomeShow>>;
}> = ({
    show,
    setShow,
}) => {
    const { data: homeSearchData, resetSearch } = useHomeSearchSettingsContext();
    const [versusCategoryId, setVersusCategoryId] = useState<number | null>(null);

    const { featuredCategoryId } = useSelectFilters();
    const { search } = useHomeContext();
    const { amongPayload } = useSelectAmongContext();
    const payload = useMemo(
        (): HomeTableRawNormRequestPayload => {
            return {
                ...amongPayload,
                normCategoryId: versusCategoryId,
            };
        },
        [amongPayload, versusCategoryId],
    );

    const {
        isLoading,
        data: rawData,
    } = useHomeTableRawNormData(payload);

    const { getFinalData, dataForSorting } = useFilteredKeywordsData(rawData, search);

    const mappedDataForSorting = useMemo(
        () => dataForSorting.map(item => ({
            ...item,
            ...item.metrics.reduce(
                (acc, metric, index) => ({
                    ...acc,
                    [`metric${index}`]: show === HomeShow.Raw ? metric.sortValue : metric.norm,
                }),
                {}
            ),
        })),
        [dataForSorting, show]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort(
        mappedDataForSorting,
        'name',
        Sort.Asc,
    );

    useEffect(() => {
        if (homeSearchData?.sortByMetric) {
            const metricIndex = HOME_TABLE_METRICS_POLARITY_ORDER.findIndex(i => i === homeSearchData.sortByMetric);

            setSortingDirection(homeSearchData.sortingDirection);
            setSortingKey(`metric${metricIndex}` as keyof HomeTableRawNormDataItem);
        }
    }, [homeSearchData, setSortingDirection, setSortingKey]);

    const data = useMemo(
        () => getFinalData(sortedData),
        [getFinalData, sortedData]
    );

    const tableConfigProps = useMemo(
        (): TableViewRawNormTableConfigProps => ({
            data,
            show,
            search,
        }),
        [data, show, search],
    );

    const tableConfig = useTableViewRawNormTableConfig(tableConfigProps);

    const tableProviderValue = useMemo(
        () => ({
            data: tableConfig,
        }),
        [tableConfig],
    );

    const tableSortingProviderValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                { id: 'name', name: 'name', defaultDirection: Sort.Asc, },
                { id: 'macStage', name: 'macStage', },
                { id: 'base', name: 'base', },
                ...HOME_TABLE_METRICS_POLARITY_ORDER.map((_, index) => ({
                    id: `metric${index}`,
                    name: `metric${index}`,
                })),
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof HomeTableRawNormDataItem);
                setSortingDirection(direction);
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection]
    );

    const copyTableContent = useCopyTableDataWithFilters(tableConfig, data.length);

    const exportPayload = useMemo(
        (): ProfileExportPptRequest => ({
            ...amongPayload,
            normCategoryId: show === HomeShow.Norm ? versusCategoryId : null,
        }),
        [amongPayload, versusCategoryId, show]
    );

    return (
        <>
            <HomeHeader resultCount={dataForSorting.length} isLoading={isLoading}>
                <SelectShow show={show} setShow={setShow}/>
            </HomeHeader>
            <HomeSubHeader>
                <SelectAmong
                    label="AMONG:"
                    useGroupsLimit
                    useCoExclusions
                    onApply={resetSearch}
                />
                {show === HomeShow.Norm && !!featuredCategoryId && (
                    <SelectVersus
                        value={versusCategoryId}
                        setValue={(value) => setVersusCategoryId(value as number)}
                        categoriesIds={[featuredCategoryId]}
                    />
                )}
            </HomeSubHeader>
            <TableSortingContext.Provider value={tableSortingProviderValue}>
                <TableColumnsContext.Provider value={tableProviderValue}>
                    <WindowScrollerTable
                        rowsCount={isLoading ? 0 : data.length}
                        headHeight={48}
                        rowHeight={42}
                        isLoading={isLoading}
                        showFallback
                    >
                        <AsideButtons>
                            <CopyTableButton
                                data={copyTableContent}
                                testId="buttonHistoricTrendCopyTable"
                            />
                        </AsideButtons>
                    </WindowScrollerTable>
                </TableColumnsContext.Provider>
            </TableSortingContext.Provider>
            <HomeActionsBar exportPayload={exportPayload}/>
        </>
    );
};

export default TableViewRawNorm;